import {
  type CSSProperties,
  useCallback,
  useRef,
  type RefObject,
  type Ref,
  useMemo,
  useId,
} from 'react'
import cx from 'classnames'
import {
  ListBox,
  ListBoxItem,
  Popover,
  type Selection,
  Autocomplete,
  useFilter,
  Virtualizer,
  ListLayout,
} from 'react-aria-components'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/Button'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { ModalSearchField } from '@/components/ModalSearchField'
import { Checkbox } from './Checkbox'
import { m, useTransform } from 'motion/react'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { ModalDialog, ModalDialogSearchView } from '../ModalDialog'
import { popoverSlideSpring } from '@/consts/springs'
import styles from './SelectOptionsPicker.module.css'
import {
  AnimationStatePropsProvider,
  useModalAnimationStateControls,
} from '@/contexts/animation-state-context'
import { ModalOverlayVariantNames } from '../ModalOverlay'
import { useScrollFadeMaskImage } from '@/hooks/scroll-fade'

interface SelectOptionsPickerProps<T extends object> {
  /**
   * The aria-label for the popover or modal dialog.
   */
  'aria-label'?: string

  /**
   * The aria-labelledby for the popover or modal dialog.
   */
  'aria-labelledby'?: string

  /**
   * The ref to the root element.
   */
  triggerRef: RefObject<HTMLElement | null>

  /**
   * The size of the trigger element.
   */
  triggerWidth: number

  /**
   * Whether the list box is a multi-select.
   */
  isMultiSelect: boolean

  /**
   * Whether the popover is open.
   */
  isOpen: boolean

  /**
   * Called when the open state changes.
   */
  onOpenChange: (isOpen: boolean) => void

  /**
   * The current search input value.
   */
  searchInputValue: string

  /**
   * Called when the search input value changes.
   */
  onSearchInputValueChange: (value: string) => void

  /**
   * The currently selected keys.
   */
  selectedKeys: Set<string>

  /**
   * Keys that should be disabled.
   */
  disabledKeys?: Set<string>

  /**
   * Called when the selection changes.
   */
  onSelectionChange: (selection: Selection) => void

  /**
   * The items to display.
   */
  items: T[]

  /**
   * Gets the key for an item.
   */
  getKey: (item: T) => string

  /**
   * Gets the label for an item.
   */
  getLabel: (item: T) => string

  /**
   * Whether the options are searchable.
   */
  isSearchable?: boolean

  /**
   * Placeholder to use in the search field.
   */
  searchPlaceholder?: string

  /**
   * Filter function to use in the search field instead of the default.
   */
  filter?: (textValue: string, inputValue: string) => boolean

  /**
   * Whether to use a popover or a modal, or to choose based on the screen size.
   *
   * @default 'auto'
   */
  as?: 'auto' | 'popover' | 'modal'

  /**
   * Test ID attribute for the close menu button, if any.
   */
  closeMenuButtonTestId?: string

  /**
   * Test ID attribute for the search input.
   */
  searchInputTestId?: string

  /**
   * Test ID attribute for the clear search button.
   */
  clearSearchButtonTestId?: string

  /**
   * Test ID attribute for dropdown items. `[key]` will be replaced by the value of `getKey`.
   */
  itemTestId?: string
}

/**
 * Options picker, chooses whether to use a popover or a modal based on
 * the screen size.
 */
export function SelectOptionsPicker<T extends object>({
  as = 'auto',
  ...props
}: SelectOptionsPickerProps<T>) {
  const isSpacious = useIsSpacious()

  if (as === 'auto') {
    return isSpacious ? (
      <SelectOptionsPickerPopover {...props} />
    ) : (
      <SelectOptionsPickerModal {...props} />
    )
  }

  if (as === 'popover') {
    return <SelectOptionsPickerPopover {...props} />
  }

  return <SelectOptionsPickerModal {...props} />
}

/**
 * Motion component for the Popover.
 */
const MotionPopover = m.create(Popover)

/**
 * The popover component for the MultiSelect.
 */
function SelectOptionsPickerPopover<T extends object>({
  triggerRef,
  triggerWidth,
  isOpen,
  onOpenChange: setIsOpen,
  searchInputValue: inputValue,
  onSearchInputValueChange: setInputValue,
  selectedKeys,
  disabledKeys,
  onSelectionChange,
  items,
  getKey,
  getLabel,
  searchPlaceholder = 'Search',
  isMultiSelect,
  isSearchable,
  filter,
  searchInputTestId,
  clearSearchButtonTestId,
  itemTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SelectOptionsPickerProps<T>) {
  const listBoxRef = useRef<HTMLDivElement>(null)
  const { scrollYStart, connectRef } = useScrollOffsets()
  const borderOpacity = useTransform(scrollYStart, [0, 8], ['0%', '100%'])
  const { contains } = useFilter({ sensitivity: 'base' })

  const effectiveFilter = useMemo(() => filter ?? contains, [filter, contains])

  const mergedRef = useComposedRefs(listBoxRef, connectRef)

  const animationStateProps = useModalAnimationStateControls({
    isOpen,
    visibleVariant: ModalOverlayVariantNames.VISIBLE,
    hiddenVariant: ModalOverlayVariantNames.HIDDEN,
  })

  return (
    <AnimationStatePropsProvider props={animationStateProps}>
      <MotionPopover
        offset={4}
        placement="bottom"
        scrollRef={listBoxRef}
        triggerRef={triggerRef}
        className={styles.popover}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        style={{ '--trigger-width': `${triggerWidth}px` } as CSSProperties}
        variants={{
          hidden: { opacity: 0, scale: 0.97 },
          visible: { opacity: 1, scale: 1 },
        }}
        transition={popoverSlideSpring}
        {...animationStateProps}
      >
        <Autocomplete filter={effectiveFilter}>
          {isSearchable && (
            <m.div
              className={styles.searchHeader}
              style={
                { '--border-color-opacity': borderOpacity } as CSSProperties
              }
            >
              <ModalSearchField
                value={inputValue}
                onChange={setInputValue}
                placeholder={searchPlaceholder}
                autoFocus
                aria-label={searchPlaceholder}
                inputTestId={searchInputTestId}
                clearButtonTestId={clearSearchButtonTestId}
              />
            </m.div>
          )}

          <SelectOptions
            disabledKeys={disabledKeys}
            items={items}
            getKey={getKey}
            getLabel={getLabel}
            isMultiSelect={isMultiSelect}
            isSearchable={isSearchable}
            ref={mergedRef}
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            aria-label={ariaLabel || searchPlaceholder}
            aria-labelledby={ariaLabelledBy}
            isDialog={false}
            itemTestId={itemTestId}
          />
        </Autocomplete>
      </MotionPopover>
    </AnimationStatePropsProvider>
  )
}

/**
 * The modal component for the MultiSelect.
 */
function SelectOptionsPickerModal<T extends object>({
  isOpen,
  onOpenChange: setIsOpen,
  searchInputValue: inputValue,
  onSearchInputValueChange: setInputValue,
  selectedKeys,
  disabledKeys,
  onSelectionChange,
  items,
  getKey,
  getLabel,
  isMultiSelect,
  searchPlaceholder = 'Search',
  filter,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  closeMenuButtonTestId,
  searchInputTestId,
  clearSearchButtonTestId,
  itemTestId,
}: SelectOptionsPickerProps<T>) {
  const close = useCallback(() => setIsOpen(false), [setIsOpen])
  const { contains } = useFilter({ sensitivity: 'base' })
  const effectiveFilter = useMemo(() => filter ?? contains, [filter, contains])

  return (
    <ModalDialog
      aria-label={ariaLabel || searchPlaceholder}
      isOpen={isOpen}
      onClose={close}
      sheetSizing="stretch"
    >
      <Autocomplete filter={effectiveFilter}>
        <ModalDialogSearchView
          onChange={setInputValue}
          value={inputValue}
          placeholder={searchPlaceholder}
          action={
            <Button
              testId={closeMenuButtonTestId}
              variant="text"
              label="Done"
              onPress={close}
            />
          }
          inputTestId={searchInputTestId}
          clearButtonTestId={clearSearchButtonTestId}
        >
          <SelectOptions
            isDialog
            disabledKeys={disabledKeys}
            items={items}
            getKey={getKey}
            getLabel={getLabel}
            isSearchable
            isMultiSelect={isMultiSelect}
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
            aria-label={ariaLabel || searchPlaceholder}
            aria-labelledby={ariaLabelledBy}
            itemTestId={itemTestId}
          />
        </ModalDialogSearchView>
      </Autocomplete>
    </ModalDialog>
  )
}

/**
 * Motion component for the ListBox.
 */
const MotionListBox = m.create(ListBox)

interface SelectOptionsProps<T extends object> {
  /**
   * The items to display in the list box.
   */
  items: T[]

  /**
   * Whether the list box is a multi-select.
   */
  isMultiSelect: boolean

  /**
   * Gets the key for an item.
   */
  getKey: (item: T) => string

  /**
   * Gets the label for an item.
   */
  getLabel: (item: T) => string

  /**
   * The ref to the list box.
   */
  ref?: Ref<HTMLDivElement | null>

  /**
   * The currently selected keys.
   */
  selectedKeys: Set<string>

  /**
   * Keys that should be disabled.
   */
  disabledKeys?: Set<string>

  /**
   * Called when the selection changes.
   */
  onSelectionChange: (selection: Selection) => void

  /**
   * The aria-label for the list box.
   */
  'aria-label'?: string

  /**
   * The aria-labelledby for the list box.
   */
  'aria-labelledby'?: string

  /**
   * Whether the options are searchable.
   */
  isSearchable?: boolean

  /**
   * Whether the options are being rendered inside a dialog.
   */
  isDialog: boolean

  /**
   * Test ID attribute for dropdown items. `[key]` will be replaced by the value of `getKey`.
   */
  itemTestId?: string
}

/**
 * The list box component for the MultiSelect.
 */
function SelectOptions<T extends object>({
  disabledKeys,
  items,
  getKey,
  getLabel,
  ref: listBoxRef,
  selectedKeys,
  onSelectionChange,
  isMultiSelect,
  isSearchable,
  isDialog,
  itemTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SelectOptionsProps<T>) {
  const {
    connectRef: connectScrollRef,
    scrollYStart,
    scrollYEnd,
  } = useScrollOffsets()
  const { maskImage } = useScrollFadeMaskImage({
    scrollYStart,
    scrollYEnd,
    fade: 'bottom',
  })
  const composedListBoxRef = useComposedRefs(listBoxRef, connectScrollRef)
  const id = useId()
  return (
    <Virtualizer layout={ListLayout} layoutOptions={{ estimatedRowHeight: 56 }}>
      <MotionListBox
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        ref={composedListBoxRef}
        shouldFocusWrap
        selectedKeys={selectedKeys}
        disabledKeys={disabledKeys}
        selectionMode={isMultiSelect ? 'multiple' : 'single'}
        className={({ isEmpty }) =>
          cx(
            styles.list,
            isSearchable && styles.searchable,
            isDialog && styles.allowInset,
            isEmpty && styles.empty,
          )
        }
        onSelectionChange={onSelectionChange}
        items={items}
        escapeKeyBehavior="none"
        shouldFocusOnHover
        style={{
          maskImage,
        }}
      >
        {
          // @ts-ignore: `children` type gets fucked by the motion wrapper
          (item: T) => {
            const key = getKey(item)
            return (
              <ListBoxItem
                id={key}
                key={key}
                textValue={getLabel(item)}
                data-test={itemTestId?.replace('[key]', key)}
                className={({ isFocused, isDisabled, isSelected }) =>
                  cx(
                    styles.option,
                    isFocused && styles.focused,
                    isDisabled && styles.disabled,
                    isSelected && styles.selected,
                  )
                }
              >
                {({ isSelected, selectionMode, isFocused, isDisabled }) => {
                  const labelId = `${id}-${getKey(item)}`
                  return (
                    <>
                      <div id={labelId} className={styles.optionText}>
                        {getLabel(item)}
                      </div>
                      {selectionMode === 'multiple' ? (
                        <Checkbox
                          readOnly
                          checked={isSelected}
                          disabled={isDisabled}
                          isFocused={isFocused}
                          aria-labelledby={labelId}
                        />
                      ) : (
                        isSelected && <Icon name="done" variant="inherit" />
                      )}
                    </>
                  )
                }}
              </ListBoxItem>
            )
          }
        }
      </MotionListBox>
    </Virtualizer>
  )
}
